import { MinimumStationsGroup, Station, StationType } from '@mi-front/core';
import {
  Autocomplete,
  AutocompleteProps,
  AutocompleteRenderGroupParams,
  Box,
  styled,
  TextField,
  Theme,
  Typography,
} from '@mui/material';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { StationFilterChip } from './display/StationFilterChip';
import {
  generateOptionId,
  getGroupHeader,
  getOptionColor,
  getOptionIcon,
  getStationTypeLabel,
  sortOptionsByType,
} from './StationFilterUtils';
import { OptionType, StationFilterOption } from './types';

// Re-export the type for backward compatibility
export type { StationFilterOption };

// Component props
interface StationsAdvancedFilterProps
  extends Pick<
    AutocompleteProps<StationFilterOption, boolean, boolean, boolean>,
    'multiple' | 'disabled'
  > {
  stations: readonly Station[];
  stationGroups?: readonly MinimumStationsGroup[];
  allowedStationTypes?: readonly StationType[];
  value?: StationFilterOption[];
  onChange: (selectedOptions: StationFilterOption[]) => void;
  placeholder?: string;
  label?: string;
  error?: boolean;
  helperText?: string;
}

const GroupHeader = styled('div')<{ bgColor?: string }>(
  ({ theme, bgColor }: { theme: Theme; bgColor?: string }) => ({
    position: 'sticky',
    top: '-8px',
    padding: '12px 8px',
    color: theme.palette.grey[800],
    backgroundColor: bgColor || theme.palette.grey[100],
    fontWeight: '500',
  }),
);

const GroupItems = styled('ul')({
  padding: 0,
  marginBottom: '12px',
});

export const StationsAdvancedFilter = ({
  stations,
  stationGroups = [],
  allowedStationTypes,
  value = [],
  onChange,
  multiple = true,
  disabled = false,
  placeholder,
  label,
  error = false,
  helperText,
}: StationsAdvancedFilterProps) => {
  const { $t } = useIntl();

  // Sort the value array for display while maintaining correct indices for deletion
  const sortedValue = useMemo(() => sortOptionsByType([...value]), [value]);

  // Handle onChange to maintain sorted order
  const handleChange = (
    event: any,
    newValue: StationFilterOption[] | StationFilterOption | null,
  ) => {
    const arrayValue = Array.isArray(newValue) ? newValue : newValue ? [newValue] : [];
    onChange(arrayValue);
  };

  // Create all available options
  const allOptions = useMemo(() => {
    const options: StationFilterOption[] = [];

    // Add station type options
    const stationTypes: StationType[] = [
      StationType.STANDALONE,
      StationType.HEADER,
      StationType.REPEATER,
      StationType.AFFILIATE,
    ];

    // Filter station types based on allowedStationTypes if provided
    const filteredStationTypes = allowedStationTypes
      ? stationTypes.filter((type) => allowedStationTypes.includes(type))
      : stationTypes;

    filteredStationTypes.forEach((type) => {
      options.push({
        type: OptionType.StationType,
        value: type,
        label: getStationTypeLabel($t, type),
      });
    });

    // Add station group options
    stationGroups.forEach((group) => {
      options.push({
        label: group.name,
        type: OptionType.StationGroup,
        value: group,
      });
    });

    // Add individual station options
    stations.forEach((station) => {
      options.push({
        label: station.name,
        type: OptionType.IndividualStation,
        value: station,
      });
    });

    return options;
  }, [stations, stationGroups, allowedStationTypes, $t]);

  // Group options by type for better organization
  const groupedOptions = useMemo(() => {
    return allOptions.sort((a, b) => {
      // First sort by type
      const typeOrder = [
        OptionType.StationType,
        OptionType.StationGroup,
        OptionType.IndividualStation,
      ];
      const aTypeIndex = typeOrder.indexOf(a.type);
      const bTypeIndex = typeOrder.indexOf(b.type);

      if (aTypeIndex !== bTypeIndex) {
        return aTypeIndex - bTypeIndex;
      }

      // Then sort alphabetically within each type
      return a.label.localeCompare(b.label);
    });
  }, [allOptions]);

  // Custom render option with icon
  const renderOption = (props: any, option: StationFilterOption) => (
    <Box component="li" {...props}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
        <Box sx={{ color: '#bbb' }}>{getOptionIcon(option.type)}</Box>
        <Typography variant="body2">{option.label}</Typography>
      </Box>
    </Box>
  );

  // Custom render tags (chips) with icons
  const renderTags = (tagValue: readonly StationFilterOption[], getTagProps: any) => {
    return tagValue.map((option, index) => {
      const { onDelete, ...tagProps } = getTagProps({ index });
      return (
        <Box key={generateOptionId(option)} {...tagProps}>
          <StationFilterChip option={option} size="medium" onDelete={onDelete} readonly={false} />
        </Box>
      );
    });
  };

  // Custom group header renderer
  const renderGroup = (params: AutocompleteRenderGroupParams) => {
    const { group, children } = params;
    const type = groupedOptions.find((opt) => getGroupHeader($t, opt.type, 'long') === group)?.type;

    return (
      <li key={group}>
        <GroupHeader bgColor={type ? getOptionColor(type) : undefined}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {type && getOptionIcon(type)}
            {group}
          </Box>
        </GroupHeader>
        <GroupItems>{children}</GroupItems>
      </li>
    );
  };

  // only show placeholder if no options are selected
  const showPlaceholder = !sortedValue.length;

  return (
    <Autocomplete
      multiple={multiple}
      disableCloseOnSelect
      filterSelectedOptions
      disabled={disabled}
      options={groupedOptions}
      value={sortedValue}
      onChange={handleChange}
      groupBy={(option) => getGroupHeader($t, option.type, 'long')}
      getOptionLabel={(option) => option.label}
      isOptionEqualToValue={(option, value) => generateOptionId(option) === generateOptionId(value)}
      renderOption={renderOption}
      renderTags={renderTags}
      renderGroup={renderGroup}
      renderInput={(params) => (
        <TextField
          {...params}
          error={error}
          helperText={helperText}
          label={
            label ??
            $t({
              id: 'ui.stationsFilter.label',
              defaultMessage: 'Filtro de emisoras',
            })
          }
          placeholder={
            showPlaceholder
              ? (placeholder ??
                $t({
                  id: 'ui.stationsFilter.placeholder',
                  defaultMessage: 'Seleccionar una o varias opciones ',
                }))
              : undefined
          }
        />
      )}
      sx={{
        minWidth: 300,
      }}
    />
  );
};
